import React, { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { SEND_DEB_FORM, UPDATE_INSTALLER_CONSENT_MUTATION } from 'Queries/Mutations';
import { createPatchEinwilligungByInstallerIdData } from 'Queries/QueriesHelper';
import { FormikProvider, useFormik } from 'formik';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';
import { useInstallerStatusSummary } from 'hooks/UseInstallerStatusSummary';
import { useFormUtilityData } from 'hooks/useFormUtilityData';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { DataPolicyContent } from 'components/DataPolicy/DataPolicyContent';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormPageWrapper } from 'components/FormHelper/FormPageWrapper';
import { FormSection } from 'components/FormHelper/FormSection';
import { InputField } from 'components/FormHelper/InputField';
import { SubmitPopup } from 'components/FormHelper/SubmitPopup';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';

import { DEB_ACTIVE_PAGE, FORM_CONSTANTS, VALIDATION_CONSTANTS } from 'global/constants';

import { FFormDebug } from 'utils/FFormDebug';
import { checkKeyDown } from 'utils/FormUtils';
import { getMyDataPath } from 'utils/UrlUtil';

import { createEmailSubjectForDebFI, prepDebFIDataForBackend } from './Utils/DebFIFormDataUtil';

import './DebForm.sass';

const DEBFI_DATAPOLICY_CONFIRMATION_TEXT =
  'Mit Absenden dieser Anfrage bestätige ich / bestätigen wir, dass ich / wir die „Datenschutz“-Zustimmung und die hier aktuell hinterlegte „Informationen zum Datenschutz“ zur Kenntnis genommen habe / haben und mit Absenden dieser Anfrage bestätige ich / bestätigen wir, dass die im Antrag genannten Personen die „Informationen zum Datenschutz“ gelesen haben und mit den in der „Datenschutz“-Zustimmung genannten Punkten einverstanden sind.';

const createDebFISecondPageInitialValues = (installer) => ({
  isAddressPublic: installer.zuAdressveroeff === 'Ja',
  canSendMail: installer.zuInfoversand === 'Ja',
  changes: '',
});

const createDebFISecondPageValidationSchema = () =>
  Yup.object().shape({
    changes: Yup.string()
      .trim()
      .nullable()
      .typeError('must be a string')
      .transform((value) => (value === '' ? null : value))
      .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(FORM_CONSTANTS.FREETEXT_INPUT, VALIDATION_CONSTANTS.TOO_LONG),
    isAddressPublic: Yup.bool().required(),
    canSendMail: Yup.bool().required(),
  });

export const DebFISecondPage = ({
  valuesFirstPage,
  initialValuesFirstPage,
  installer,
  setPage,
  dataPolicyDocuments,
}) => {
  const { refetchInstaller } = useCurrentInstaller();
  const installerStatusSummary = useInstallerStatusSummary(installer);
  const { comboBoxOptions } = useFormUtilityData();

  const initialValues = useMemo(() => createDebFISecondPageInitialValues(installer), [installer]);
  const validationSchema = useMemo(() => createDebFISecondPageValidationSchema(), []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
  });

  const { values, errors, touched, handleSubmit } = formik;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [response, setResponse] = useState('');
  const [openPopup, setOpenPopup] = useState(false);

  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);
  const [sendDebForm] = useMutation(SEND_DEB_FORM);
  const [updateInstallerConsentMutation] = useMutation(UPDATE_INSTALLER_CONSENT_MUTATION);

  const onSubmit = async () => {
    setIsSubmitting(true);
    const subject = createEmailSubjectForDebFI(
      valuesFirstPage,
      initialValuesFirstPage,
      installerStatusSummary
    );
    const formData = prepDebFIDataForBackend(
      initialValuesFirstPage,
      valuesFirstPage,
      values,
      subject,
      installerStatusSummary,
      comboBoxOptions
    );

    try {
      const patchData = createPatchEinwilligungByInstallerIdData(
        values.isAddressPublic,
        values.canSendMail
      );
      if (
        patchData.zuAdressveroeff !== installer.zuAdressveroeff ||
        patchData.zuInfoversand !== installer.zuInfoversand
      ) {
        await updateInstallerConsentMutation({
          variables: patchData,
        });
      }
    } catch (e) {
      console.error(e);
    }

    sendDebForm({
      variables: {
        debInput: formData,
      },
    })
      .then(({ data }) => {
        setOpenPopup(true);
        setResponse(data.deb.status);
      })
      .catch(() => {
        setOpenPopup(true);
        setResponse('ERROR');
      })
      .finally(() => {
        setIsSubmitting(false);
        refetchInstaller();
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <FormikProvider value={formik}>
      <form onKeyDown={checkKeyDown} onSubmit={handleSubmit} className="deb-form__second-page">
        <FormPageWrapper>
          <FormSection key="changes" title="Weitere Angaben">
            <div>
              Sie haben noch Fragen oder weitere Anmerkungen? Dann können Sie uns hier eine
              Nachrichten hinterlassen.
            </div>
            <InputField
              key="changes"
              name="changes"
              fullWidth
              label="Freitext"
              oldValue=""
              variant="standard"
              value={values.changes}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.changes)}
              helperText={formik.errors.changes || ''}
              multiline
              rows={6}
              rowsMax={6}
            />
          </FormSection>
          <FormSection key="changesAndPrivacy" title="Datenschutz">
            <div>Hier können Sie ihre Zustimmungen bearbeiten</div>
            <div className="agreement-section__wrapper">
              <DataPolicyContent
                confirmationText={DEBFI_DATAPOLICY_CONFIRMATION_TEXT}
                dsgvoAddressPublic={values.isAddressPublic}
                dsgvoCanSendMail={values.canSendMail}
                dsgvoAddressPublicName="isAddressPublic"
                dsgvoCanSendMailName="canSendMail"
                onChangeDsvgoAddressPublic={formik.handleChange}
                onChangeDsvgoCanSendMail={formik.handleChange}
                documents={dataPolicyDocuments}
                showAddressPublicApproval={false}
              />
            </div>
          </FormSection>
        </FormPageWrapper>

        {isSubmitting ? (
          <LoadingIndicator />
        ) : (
          <FormActionButtons
            errorMessage="Bitte füllen Sie alle mit * gekennzeichneten Pflichtfelder aus."
            hasErrors={hasErrors}
            titleLeft="Zurück"
            titleRight="Absenden"
            onClickLeft={() => {
              setPage(DEB_ACTIVE_PAGE.FORM_PAGE);
            }}
            onClickRight={() => {
              onSubmit();
            }}
          />
        )}

        <FFormDebug values={values} errors={errors} touched={touched} />
      </form>
      <SubmitPopup
        type="EDIT_COMPANY"
        loading={isSubmitting}
        popupOpen={openPopup}
        setPopupOpen={() => setOpenPopup(false)}
        response={response}
        redirectLinkOnSuccessOrCancel={getMyDataPath()}
        onErrorRetry={() => {
          onSubmit();
        }}
      />
    </FormikProvider>
  );
};

DebFISecondPage.displayName = 'DebFISecondPage';

DebFISecondPage.propTypes = {
  valuesFirstPage: PropTypes.object.isRequired,
  initialValuesFirstPage: PropTypes.object.isRequired,
  installer: PropTypes.object.isRequired,
  setPage: PropTypes.func.isRequired,
  dataPolicyDocuments: PropTypes.array.isRequired,
};

DebFISecondPage.defaultProps = {};
